// src/hooks/useMarket.ts
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type {
  MarketHealth,
  GateDecision,
  CoreAssetsResponse,
  SentimentData,
  OrderbookRow,
  MoversResponse,
  WhitelistResponse,
  SymbolDetailResponse,
  MarketDashboardResponse,
  RefreshIntervalsConfig,
  SessionResponse,
} from "@/types/market";

// Centralized refresh defaults (seconds) — will be overridden by server config if available
const DEFAULT_INTERVALS: Required<RefreshIntervalsConfig> = {
  snapshot: 90,
  health: 40,
  orderbook: 15,
  sentiment: 180,
  movers: 90,
};

// Helper to unwrap backend { success, data } responses (falls back gracefully)
async function unwrap<T = any>(res: Response): Promise<T> {
  const j: any = await res.json().catch(() => ({}));
  if (j && typeof j === 'object' && 'data' in j) return j.data as T;
  return j as T;
}

// Fetch refresh config once (stale rarely)
export function useRefreshIntervals() {
  return useQuery<RefreshIntervalsConfig>({
    queryKey: ["/api/market/refresh-interval"],
    queryFn: async () => {
      try {
        const r = await apiRequest("GET", "/api/market/refresh-interval");
        const raw = await unwrap<any>(r);
        const data = raw || {};
        // Map backend keys to our internal ones
        return {
          snapshot: data.full_snapshot,
          health: data.market_health,
          orderbook: data.orderbook,
          sentiment: data.sentiment,
            movers: data.movers,
        } as RefreshIntervalsConfig;
      } catch {
        return DEFAULT_INTERVALS; // fallback
      }
    },
    // Inherit always-fresh defaults
  });
}

function ms(sec?: number | false): number | false {
  if (!sec) return false;
  return sec * 1000;
}

export function useMarketHealth() {
  const { data: cfg } = useRefreshIntervals();
  return useQuery<MarketHealth>({
    queryKey: ["/api/market/health"],
  queryFn: async () => unwrap<MarketHealth>(await apiRequest("GET", "/api/market/health")),
    refetchInterval: ms(cfg?.health ?? DEFAULT_INTERVALS.health),
  });
}

export function useGateDecision() {
  const { data: cfg } = useRefreshIntervals();
  return useQuery<GateDecision>({
    queryKey: ["/api/market/pre-analysis"],
  queryFn: async () => unwrap<GateDecision>(await apiRequest("GET", "/api/market/pre-analysis")),
    refetchInterval: ms(Math.max((cfg?.health ?? DEFAULT_INTERVALS.health) * 2, 60)),
  });
}

export function useCoreAssets(symbols: string[] = ["BTCUSDT", "ETHUSDT"]) {
  const { data: cfg } = useRefreshIntervals();
  const qs = symbols.join(",");
  return useQuery<CoreAssetsResponse>({
    queryKey: ["/api/market/core-assets", qs],
    queryFn: async () => {
      const raw = await unwrap<any>(await apiRequest("GET", `/api/market/core-assets?symbols=${encodeURIComponent(qs)}`));
      // Backend returns analysis dict directly – wrap for UI consistency
      return { core_assets: raw } as CoreAssetsResponse;
    },
    refetchInterval: ms( (cfg?.health ?? DEFAULT_INTERVALS.health) ),
  });
}

export function useSentiment() {
  const { data: cfg } = useRefreshIntervals();
  return useQuery<SentimentData>({
    queryKey: ["/api/market/sentiment"],
    queryFn: async () => {
      const raw = await unwrap<any>(await apiRequest("GET", "/api/market/sentiment"));
      // If backend wraps under a 'sentiment' key after unwrapping, drill into it.
      const base = raw && typeof raw === 'object' && 'sentiment' in raw && raw.sentiment
        ? raw.sentiment
        : raw;
      return base as SentimentData;
    },
    refetchInterval: ms(cfg?.sentiment ?? DEFAULT_INTERVALS.sentiment),
  });
}

export function useOrderbook(symbol: string) {
  const { data: cfg } = useRefreshIntervals();
  return useQuery<OrderbookRow>({
    queryKey: ["/api/market/orderbook", symbol],
    enabled: !!symbol,
  queryFn: async () => unwrap<OrderbookRow>(await apiRequest("GET", `/api/market/orderbook?symbol=${symbol}`)),
    refetchInterval: ms(cfg?.orderbook ?? DEFAULT_INTERVALS.orderbook),
  });
}

export function useMovers() {
  const { data: cfg } = useRefreshIntervals();
  return useQuery<MoversResponse>({
    queryKey: ["/api/market/movers", { full: true }],
    queryFn: async () => {
      const raw = await unwrap<any>(await apiRequest("GET", "/api/market/movers?full_details=true"));
      return { movers: raw } as MoversResponse;
    },
    refetchInterval: ms(cfg?.movers ?? DEFAULT_INTERVALS.movers),
  });
}

export function useWhitelist() {
  return useQuery<WhitelistResponse>({
    queryKey: ["/api/market/whitelist"],
  queryFn: async () => unwrap<WhitelistResponse>(await apiRequest("GET", "/api/market/whitelist")),
    // Inherit always-fresh defaults
  });
}

export function useSymbolDetail(symbol?: string, opts: { includeOrderbook?: boolean; includeTechnical?: boolean } = {}) {
  const { includeOrderbook = true, includeTechnical = true } = opts;
  return useQuery<SymbolDetailResponse>({
    queryKey: ["/api/market/symbol", symbol, includeOrderbook, includeTechnical],
    enabled: !!symbol,
    queryFn: async () => {
      if (!symbol) throw new Error("no-symbol");
      const url = `/api/market/symbol/${symbol}?include_orderbook=${includeOrderbook}&include_technical=${includeTechnical}`;
  return unwrap<SymbolDetailResponse>(await apiRequest("GET", url));
    },
    refetchInterval: ms(45),
  });
}

export function useMarketDashboard() {
  return useQuery<MarketDashboardResponse>({
    queryKey: ["/api/market/dashboard"],
    queryFn: async () => {
      const raw = await unwrap<any>(await apiRequest("GET", "/api/market/dashboard"));
      // Raw already has nested data structure; pass through for now.
      return raw as MarketDashboardResponse;
    },
    refetchInterval: ms(120),
  });
}

export function useSessionPulse() {
  return useQuery<SessionResponse>({
    queryKey: ["/api/market/session"],
  queryFn: async () => unwrap<SessionResponse>(await apiRequest("GET", "/api/market/session")),
    refetchInterval: ms(60),
  });
}
